import { useAppDispatch } from "../redux/hooks";
import React, { useEffect, useRef, useState } from "react";
import { getEndsInLabel } from "../../helpers/endsIn";
import {
  publishModalClasses,
  publishModalData,
  publishModalProps,
  publishModalVisibility,
} from "../redux/features/modal/modalSlice";
import TicketModalPopup from "../components/PrizeTicketSelector/prizeTicketSelector";
import { getCookie } from "../../helpers/cookies";
import { useIsUserSubscriber, useSubscriptionLobbyContent } from "../redux/features/header/selectors";
import { CompetitionData } from "../components/ActiveCompetitions/types";
import { CompetitionCategory } from "../redux/features/categories/slice";
import { useIsMobile } from "../redux/features/device/selectors";
import SubscriptionPlansModal from "../(custom-header)/subscriber-competitions/components/subscriptionPlansModal";

export default function useCompetitionItem(
  item: CompetitionData,
  isUserValidated: boolean,
  category: CompetitionCategory,
  addToCartMethodId?: number
) {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const isFullWidthRequired = useRef<boolean>(false);
  const endsIn = getEndsInLabel(item.EndDate);

  const onAddToBasketCTAClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(
      publishModalData({
        modalData: <TicketModalPopup prizeId={item.CtaAddToBasketPrizeId} addToCartMethodId={addToCartMethodId} />,
      })
    );
    dispatch(
      publishModalClasses({
        classNames: "md:rounded-2xl xs:absolute xs:bottom-0 xs:w-full xs:rounded-b-none xs:rounded-t-2xl z-10",
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  useEffect(() => {
    const fromQuery = new URLSearchParams(location.search).get("navigateTo");
    const fromHash = location.hash.replace(/^#/, "") || null;
    const locationToScrollTo = fromQuery || fromHash;

    if (locationToScrollTo) {
      const header = document.getElementById("botb-header") || document.querySelector(".mainHeader");
      const categoryMenu = document.getElementById("category-menu");
      const element = document.getElementById(locationToScrollTo);
      if (element && header && categoryMenu) {
        const headerHeight = header.getBoundingClientRect().height;
        const categoryMenuHeight = categoryMenu.getBoundingClientRect().height;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - headerHeight - categoryMenuHeight - 16;
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, []);

  const getButtonText = () => {
    isFullWidthRequired.current = false;
    const isAuth = getCookie("BotB.Authenticated") === "true";
    if (item.IsRewardCompetition) {
      if (isAuth) {
        if (isUserValidated) {
          if (item.CompetitionPictureTypeId === 1) {
            return "PLAY NOW";
          }
          return "ENTER NOW";
        } else {
          isFullWidthRequired.current = true;
          if (isMobile) {
            return "GET FREE TICKET";
          }
          return "VALIDATE ACCOUNT & GET A FREE TICKET";
        }
      } else {
        isFullWidthRequired.current = true;
        if (isMobile) {
          return "GET FREE TICKET";
        }
        return "SIGN UP AND GET A FREE TICKET";
      }
    } else {
      if (item.CompetitionPictureTypeId === 1) {
        return "PLAY NOW";
      }
      return "ENTER NOW";
    }
  };

  const isSkilledCompetitionOnHomepage =
    item.CompetitionPictureTypeId === 1 && location.pathname === "/" && category.type === "featured";

  const isAlreadySubscribed = useIsUserSubscriber();

  const subscriptionLobbyContent = useSubscriptionLobbyContent();
  const youveEnteredEntry = subscriptionLobbyContent?.youveEntered.find(
    elem => Number(elem.compid) === item.CompetitionId
  );

  const getStrokeColor = () => {
    if (isSkilledCompetitionOnHomepage) return "#FFFFFF";
    return isButtonHovered ? "#FFFFFF" : "#FF8200";
  };

  const ctaButtonText = getButtonText();

  const isSCCompetition = item.CompetitionTypePrefix === "SC";

  const onCompetitionClick = () => {
    if (isSCCompetition) {
      dispatch(
        publishModalData({
          modalData: <SubscriptionPlansModal />,
        })
      );
      dispatch(
        publishModalProps({
          withOverlay: false,
        })
      );
      dispatch(publishModalVisibility({ isOpen: true }));
    } else {
      location.href = item.PageUrl;
    }
  };

  return {
    endsIn,
    onAddToBasketCTAClick,
    setIsButtonHovered,
    isMobile,
    isAlreadySubscribed,
    youveEnteredEntry,
    getStrokeColor,
    ctaButtonText,
    isSCCompetition,
    isSkilledCompetitionOnHomepage,
    isFullWidthRequired,
    onCompetitionClick,
  };
}
